/**
 * Client-authored shapes for the uploads slice of the versioned REST
 * surface (`/api/sdk/v1/uploads`): the direct-to-blob flow (plans/002) —
 * mint a write-only SAS, PUT bytes straight to blob storage, finalize.
 * Mirrors `UploadService.createDirectUpload` / `.get` returns and the
 * `upload.createDirectUpload` tRPC input. Authored copies, server
 * remains the source of truth (same trust model as `./turn`). Dates are
 * ISO 8601 strings (plain JSON, no superjson).
 */

import type { ProcessingStatus } from "./values";

/**
 * Where the upload attaches. Mirrors the target discriminated union the
 * `upload.createDirectUpload` tRPC procedure accepts (chat/workspace).
 * Skill uploads have a separate staging lifecycle and are NOT here.
 */
export type SdkUploadTarget =
  | { chatId: string; kind: "chat" }
  | { kind: "workspace"; workspaceId: string };

/** Phase 1 input: file metadata only — bytes never transit the server. */
export interface SdkUploadCreateInput {
  filename: string;
  mediaType: string;
  /** Byte size; the server's per-file cap rejects oversize metadata. */
  size: number;
  target: SdkUploadTarget;
}

/**
 * Phase 1 result: the reserved row plus the write-only SAS URL the
 * client PUTs bytes to (header `x-ms-blob-type: BlockBlob`).
 */
export interface SdkUploadMinted {
  /** Backend storage key — surfaced so the client can derive sandbox paths. */
  storageKey: string;
  uploadId: string;
  /** Write-only SAS URL for the byte PUT. */
  uploadUrl: string;
}

/** One upload's status (polling after mint/finalize). */
export interface SdkUploadStatus {
  createdAt: string;
  filename: string;
  id: string;
  mediaType: string;
  /** Docling pipeline status (ADR-055); null for non-processable files. */
  processingStatus?: ProcessingStatus | null;
  /** Tier-2 (rich pass) failure flag — paired with `text-ready`. */
  richPassFailed?: boolean | null;
  size: number;
  url: string;
}

/** Processing snapshot for terminality checks (ADR-055). */
export interface SdkUploadProcessingSnapshot {
  richPassFailed?: boolean | null;
  status: ProcessingStatus | null | undefined;
}

/**
 * Is the processing pipeline done with this upload (polls can stop, the
 * composer's text-ready gate lifts)? Authored copy of the server's
 * `isUploadProcessingTerminal` (`@workspace/services`) — terminality is
 * wire semantics, so the contract carries it for SDK consumers that can't
 * import the services package. `text-ready` + `richPassFailed` is
 * terminal (tier-2 needs a user retry).
 */
export function isSdkUploadProcessingTerminal(
  snapshot: SdkUploadProcessingSnapshot | null | undefined
): boolean {
  if (!snapshot) {
    return true;
  }
  const { status, richPassFailed } = snapshot;
  if (status == null) {
    return true;
  }
  // Parked until a backfill supplies the original bytes — nothing to poll.
  if (status === "awaiting-source") {
    return true;
  }
  if (status === "completed" || status === "failed") {
    return true;
  }
  return status === "text-ready" && richPassFailed === true;
}

/**
 * Phase 3 result: the finalized upload row — the post-finalize snapshot
 * the UI renders (`url`/`storageKey` for sandbox paths and document
 * parts, `processingStatus` for the processing indicator). Mirrors the
 * `UploadResult` that `UploadService.finalizeDirectUpload` returns and
 * the finalize route serializes; `processingStatus` is null for
 * non-processable files (no pipeline run).
 */
export interface SdkUploadFinalized {
  filename: string;
  id: string;
  mediaType: string;
  processingStatus: ProcessingStatus | null;
  size: number;
  /** Backend storage key — surfaced so the client can derive sandbox paths. */
  storageKey: string;
  url: string;
}

// ---------------------------------------------------------------------------
// Composer staging limits (plan 023). The block's attachments seam carries
// these as data so hosts and the SDK agree with the server-side validation in
// `@workspace/storage` without importing it (the contract must stay
// dependency-free). Values are pinned against the server constants by the
// `upload-constants-sync` anti-drift test in `@magnus/react`.
// ---------------------------------------------------------------------------

/** Largest chat attachment batch the composer stages. Mirrors
 *  `MAX_FILES_PER_CHAT` in `@workspace/storage`. */
export const maxFilesPerChat = 10;

/**
 * `<input accept>` values — MIME types + bare extensions (so the OS picker
 * finds `.xer`/`.md`/`.py`). Mirrors `ACCEPT_TYPES` in `@workspace/storage`
 * (same order: all uploadable MIMEs, then all extensions).
 */
export const uploadAcceptTypes = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/heic",
  "image/heif",
  "image/svg+xml",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "text/markdown",
  "application/vnd.oasis.opendocument.text",
  "application/rtf",
  "application/epub+zip",
  "application/vnd.ms-outlook",
  "text/csv",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel.sheet.macroEnabled.12",
  "application/json",
  "text/html",
  "text/xml",
  "application/xml",
  "text/x-python",
  "text/yaml",
  "text/css",
  "application/javascript",
  "text/javascript",
  "application/typescript",
  "text/x-shellscript",
  "application/vnd.alteryx.workflow+xml",
  "application/vnd.alteryx.macro+xml",
  "application/vnd.alteryx.app+xml",
  "application/vnd.alteryx.app+zip",
  "application/zip",
  "application/x-zip-compressed",
  "application/x-zip",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "model/stl",
  "model/3mf",
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".webp",
  ".heic",
  ".heif",
  ".svg",
  ".pdf",
  ".docx",
  ".txt",
  ".md",
  ".odt",
  ".rtf",
  ".epub",
  ".msg",
  ".csv",
  ".xlsx",
  ".xlsm",
  ".json",
  ".html",
  ".htm",
  ".xml",
  ".xer",
  ".xsd",
  ".py",
  ".yml",
  ".yaml",
  ".css",
  ".js",
  ".jsx",
  ".ts",
  ".tsx",
  ".sh",
  ".yxmd",
  ".yxmc",
  ".yxwv",
  ".yxwz",
  ".zip",
  ".pptx",
  ".stl",
  ".3mf",
] as const;
